import { ReactElement, ReactNode, useEffect, useState } from "react";
import { Button, Spin, Tooltip, Typography, notification } from "antd";
import clsx from "clsx";
import { useTitleSectionsStore, type SectionId } from "@/features/journal-title-sections";
import { InfoIcon, VectorIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { triggerUnifiedResize, NOTIFICATION_MESSAGES } from "@shared/utils";
import { titleSectionsConfig } from "../journals-title/model/title-sections-config";
import "./collapsible-section.scss";

type ValidationError = {
  id: number;
  description: string | ReactElement;
};

interface CollapsibleSectionProps {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  spinning?: boolean;
  validationErrors?: ValidationError[];
  sectionId?: SectionId;
  getValidationErrorsForSection?: (sectionId: SectionId) => ValidationError[];
  saveBeforeNext?: boolean;
  onSave?: () => Promise<void>;
  isEmpty?: boolean;
}

export const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
  spinning,
  validationErrors = [],
  sectionId,
  getValidationErrorsForSection,
  saveBeforeNext,
  onSave,
  isEmpty = false,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isSaving, setIsSaving] = useState(false);
  const toggleSection = useTitleSectionsStore((state) => state.toggleSection);
  const setOpenedSection = useTitleSectionsStore(
    (state) => state.setOpenedSection,
  );
  const isCurrentlyOpen = useTitleSectionsStore((state) =>
    sectionId ? state.isCurrentlyOpen(sectionId) : false,
  );

  useEffect(() => {
    if (sectionId) {
      setOpenedSection(sectionId, isOpen);
    }
  }, [isOpen, sectionId, setOpenedSection]);

  useEffect(() => {
    if (sectionId && isCurrentlyOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [isCurrentlyOpen, sectionId, isOpen]);

  const hasErrors = validationErrors.length > 0;

  const getStatusDotClass = () => {
    if (isEmpty) return "collapsible-section__status-dot--empty";
    if (hasErrors) return "collapsible-section__status-dot--error";
    return "collapsible-section__status-dot--ok";
  };

  const getTooltipContent = () => {
    if (hasErrors) {
      return (
        <div>
          {validationErrors.map((error) => (
            <div key={error.id} className="collapsible-section-tooltip-item">
              {error.description}
            </div>
          ))}
        </div>
      );
    }
    return "Все изменения сохранятся. Перед отправкой ТЛ на проверку обязательно заполнить этит блок.";
  };

  const handleToggle = () => {
    if (!isOpen && sectionId) {
      toggleSection(sectionId);
    }
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (sectionId && !newIsOpen) {
      setOpenedSection(sectionId, false);
    }
    setTimeout(() => {
      triggerUnifiedResize();
    }, 500);
  };

  const getScrollableContainer = (el: Element | null): HTMLElement => {
    let current: HTMLElement | null = el ? (el as HTMLElement).parentElement : null;
    while (current) {
      const { overflowY } = window.getComputedStyle(current);
      const hasScrollableContent = current.scrollHeight > current.clientHeight;
      if (
        hasScrollableContent &&
        (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay")
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return (document.scrollingElement as HTMLElement) || document.documentElement;
  };

  const handleNext = async () => {
    if (!sectionId || !getValidationErrorsForSection) return;

    if (saveBeforeNext && onSave) {
      setIsSaving(true);
      try {
        await onSave();
        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS, // "Успешно"
          description: NOTIFICATION_MESSAGES.DATA_SAVED_DESC, // "Данные сохранены"
        });
        setIsSaving(false);
      } catch (error) {
        setIsSaving(false);
        return;
      }
    }

    const currentIndex = titleSectionsConfig.findIndex(
      (s) => s.id === sectionId,
    );

    const nextIndex = currentIndex + 1;
    if (nextIndex < titleSectionsConfig.length) {
      setIsOpen(false);
      setOpenedSection(sectionId, false);
      const nextSection = titleSectionsConfig[nextIndex];
      setTimeout(() => {
        const element = document.getElementById(nextSection.id);
        if (element) {
          const header = element.querySelector(".collapsible-section__header");
          if (header) {
            (header as HTMLElement).click();
          }
          const container = getScrollableContainer(element);
          container.scrollBy({ top: 120, behavior: "smooth" });
        }
      }, 100);
    } else {
      const currentEl = document.getElementById(sectionId);
      if (currentEl) {
        currentEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <Spin spinning={spinning || isSaving}>
      <div className="collapsible-section table-container" id={sectionId || ""}>
        <div
          className="collapsible-section__header"
          onClick={handleToggle}
        >
          <div className="collapsible-section__header-main">
            <div
              className={clsx(
                "collapsible-section__status-dot",
                getStatusDotClass(),
              )}
            />
            <Typography.Title
              level={4}
              className={clsx(
                "collapsible-section__title",
                isOpen && "collapsible-section__title--open",
              )}
            >
              {title}
            </Typography.Title>
          </div>
          <div className="collapsible-section__toggle-icon-wrap">
            <VectorIcon
              className={clsx(
                "collapsible-section__icon",
                !isOpen && "collapsible-section__icon--rotated",
              )}
            />
          </div>
        </div>
        <div
          className={clsx("collapsible-section__content", isOpen && "collapsible-section__content--open")}
          onTransitionEnd={(e) => {
            // Триггерим resize когда заканчивается transition на max-height
            if (e.propertyName === 'max-height') {
              triggerUnifiedResize();
            }
          }}
        >
          <div className="collapsible-section__content-inner">{children}</div>
          <div className="collapsible-section__footer-actions">
            <Can I="edit" a="title_journals">
              <Button type="primary" disabled={isSaving} onClick={handleNext}>
                Далее
              </Button>
              <Tooltip title={getTooltipContent()}>
                <InfoIcon />
              </Tooltip>
            </Can>
          </div>
        </div>
      </div>
    </Spin>
  );
};
